/**
 * Robot Entity - Mobile robot with manipulator
 * Manages robot location, grippers, and interactions with other entities
 */

/**
 * Parse item to extract base name and position
 */
function parseItem(item) {
    const match = item.match(/^(.+?)\(([^)]+)\)$/);
    if (match) {
        return {
            baseName: match[1].trim(),
            position: match[2],
            fullName: item
        };
    }
    return {
        baseName: item,
        position: null,
        fullName: item
    };
}

/**
 * Get position from item
 */
function getPosition(item) {
    const parsed = parseItem(item);
    return parsed.position;
}

/**
 * Add position to item
 */
function addPosition(item, position) {
    const baseName = parseItem(item).baseName;
    if (position) {
        return `${baseName}(${position})`;
    }
    return baseName;
}

/**
 * Compare positions for sorting
 */
function comparePositions(posA, posB) {
    if (!posA && !posB) return 0;
    if (!posA) return 1;
    if (!posB) return -1;

    const parsePos = (pos) => {
        const match = pos.match(/(\d+),\s*(\d+),\s*(\d+)/);
        if (!match) return [0, 0, 0];
        return [parseInt(match[1]), parseInt(match[2]), parseInt(match[3])];
    };

    const [x1, y1, z1] = parsePos(posA);
    const [x2, y2, z2] = parsePos(posB);

    if (x1 !== x2) return x1 - x2;
    if (y1 !== y2) return y1 - y2;
    return z1 - z2;
}

/**
 * Sort items by name first, then by position
 */
function sortItems(items) {
    return items.sort((a, b) => {
        const itemA = parseItem(a);
        const itemB = parseItem(b);

        const nameComparison = itemA.baseName.localeCompare(itemB.baseName);
        if (nameComparison !== 0) {
            return nameComparison;
        }

        return comparePositions(itemA.position, itemB.position);
    });
}

class Robot {
    constructor() {
        this.location = "cargador";
        this.armState = "reposo";
        this.contains = []; // Items carried by grippers
        this.indexPieza = 1; // Counter for piece numbering
        this.maxCapacity = 6; // Maximum items robot can carry
        this.consumedRaws = []; // List of raw materials consumed in machining
    }

    /**
     * Check if robot has space to pick up more items
     * @returns {boolean} - true if space available, false if full
     */
    hasCapacity() {
        return this.contains.length < this.maxCapacity;
    }

    /**
     * Get remaining capacity
     * @returns {number} - Number of free slots
     */
    getRemainingCapacity() {
        return this.maxCapacity - this.contains.length;
    }

    /**
     * Move robot to a new location
     * @param {string} destination - Target location
     */
    moveTo(destination) {
        this.location = destination;
    }

    /**
     * Get current location
     * @returns {string}
     */
    getLocation() {
        return this.location;
    }

    /**
     * Set arm state/configuration
     * @param {string} state - Arm state (reposo, extendido0-5, etc.)
     */
    setArmState(state) {
        this.armState = state;
    }

    /**
     * Get arm state
     * @returns {string}
     */
    getArmState() {
        return this.armState;
    }

    /**
     * Pick up an item from a location entity
     * @param {Object} fromEntity - Entity to pick from (Almacen, Ikasmak)
     * @param {string} item - Item to pick up
     * @param {string} identifier - Additional identifier (position, etc.)
     * @returns {boolean} - Success status
     */
    pickFromEntity(fromEntity, item, identifier = "") {
        if (!fromEntity.isOpen || (fromEntity.isOpen && !fromEntity.isOpen())) {
            return false; // Entity must be open
        }

        // Check capacity before picking up
        if (!this.hasCapacity()) {
            console.log(`[Robot.pickFromEntity] FAILED - Robot at max capacity (${this.maxCapacity}/${this.maxCapacity})`);
            return false;
        }

        const fullItem = identifier ? item + identifier : item;
        this.contains.push(fullItem);
        console.log(`[Robot.pickFromEntity] Picked up "${fullItem}" (${this.contains.length}/${this.maxCapacity})`);
        fromEntity.removeItem(fullItem);
        return true;
    }

    /**
     * Place an item into a location entity
     * @param {Object} toEntity - Entity to place into (Almacen, Ikasmak)
     * @param {string} item - Item to place (optional, places last if not specified)
     * @returns {boolean} - Success status
     */
    placeIntoEntity(toEntity, item = null) {
        console.log(`[Robot.placeIntoEntity] Searching for item: "${item}"`);
        console.log(`[Robot.placeIntoEntity] Robot.contains:`, JSON.stringify(this.contains));

        if (!toEntity.isOpen || (toEntity.isOpen && !toEntity.isOpen())) {
            console.log(`[Robot.placeIntoEntity] Entity not open`);
            return false; // Entity must be open
        }

        if (this.contains.length === 0) {
            console.log(`[Robot.placeIntoEntity] Robot contains is empty`);
            return false; // Nothing to place
        }

        let itemToPlace;
        if (item) {
            // Find specific item
            const index = this.contains.findIndex(i => i.includes(item));
            console.log(`[Robot.placeIntoEntity] Found at index: ${index}`);
            if (index !== -1) {
                itemToPlace = this.contains.splice(index, 1)[0];
                console.log(`[Robot.placeIntoEntity] Removed item: "${itemToPlace}"`);
            } else {
                console.log(`[Robot.placeIntoEntity] Item "${item}" not found in robot.contains`);
                return false;
            }
        } else {
            // Place last item
            itemToPlace = this.contains.pop();
            console.log(`[Robot.placeIntoEntity] Popped last item: "${itemToPlace}"`);
        }

        toEntity.addItem ? toEntity.addItem(itemToPlace) : toEntity.loadItem(itemToPlace);
        console.log(`[Robot.placeIntoEntity] Added "${itemToPlace}" to entity`);
        return true;
    }

    /**
     * Pick up raw material from Almacen
     * @param {Almacen} almacen - Almacen entity
     * @param {string} bruto - Raw material type
     * @param {string} pos - Position in almacen
     * @returns {boolean}
     */
    pickRaw(almacen, bruto, pos) {
        return this.pickFromEntity(almacen, bruto, pos);
    }

    /**
     * Pick up tool from Almacen
     * @param {Almacen} almacen - Almacen entity
     * @param {string} herramienta - Tool ID
     * @param {string} pos - Position in almacen
     * @returns {boolean}
     */
    pickTool(almacen, herramienta, pos) {
        return this.pickFromEntity(almacen, herramienta, pos);
    }

    /**
     * Place raw material into Ikasmak
     * @param {Ikasmak} ikasmak - Ikasmak entity
     * @param {string} bruto - Raw material type (for matching)
     * @param {Set} knownRaws - Optional set of known raw material names from BT
     * @returns {boolean}
     */
    placeRawIkasmak(ikasmak, bruto, knownRaws = null) {
        if (!ikasmak.isOpen()) {
            return false;
        }

        // Find raw material in robot's inventory
        let rawIndex = -1;

        if (knownRaws && knownRaws.size > 0) {
            // Use known raw materials from BT XML
            rawIndex = this.contains.findIndex(item => {
                for (const rawName of knownRaws) {
                    if (item.includes(rawName)) {
                        return true;
                    }
                }
                return false;
            });
        } else {
            // Fallback: identify by exclusion (not tool, not piece)
            rawIndex = this.contains.findIndex(item => {
                const itemLower = item.toLowerCase();
                const isTool = /^T\d+/i.test(item);
                const isPiece = itemLower.includes('piece') || itemLower.includes('pieza');
                return !isTool && !isPiece;
            });
        }

        if (rawIndex >= 0) {
            const rawMaterial = this.contains[rawIndex];
            // Keep the full name WITH position tag
            // NO longer stripping position - it's preserved throughout

            // Try to load in Ikasmak (will check capacity) with FULL name including position
            const loaded = ikasmak.loadItem(rawMaterial);
            if (!loaded) {
                console.log(`[Robot.placeRawIkasmak] FAILED - Ikasmak at max raw capacity`);
                return false;
            }

            // Only remove from robot if successfully loaded
            this.contains.splice(rawIndex, 1);
            console.log(`[Robot.placeRawIkasmak] Placed raw WITH position: "${rawMaterial}"`);
            return true;
        }

        return false;
    }

    /**
     * Pick up finished piece from Ikasmak
     * Picks currentPiece from Ikasmak and clears it
     * @param {Ikasmak} ikasmak - Ikasmak entity
     * @returns {boolean}
     */
    pickUpRawIkasmak(ikasmak) {
        console.log(`[Robot.pickUpRawIkasmak] START`);
        console.log(`[Robot.pickUpRawIkasmak] Ikasmak currentPiece BEFORE:`, ikasmak.currentPiece);

        if (!ikasmak.isOpen()) {
            console.log(`[Robot.pickUpRawIkasmak] FAILED - Ikasmak not open`);
            return false;
        }

        // Pick up piece from ikasmak.currentPiece
        if (ikasmak.currentPiece) {
            // Check capacity before picking up
            if (!this.hasCapacity()) {
                console.log(`[Robot.pickUpRawIkasmak] FAILED - Robot at max capacity (${this.maxCapacity}/${this.maxCapacity})`);
                return false;
            }

            const piece = ikasmak.currentPiece;
            this.contains.push(piece);
            ikasmak.currentPiece = null; // Clear piece from Ikasmak
            console.log(`[Robot.pickUpRawIkasmak] Picked up piece: "${piece}" (${this.contains.length}/${this.maxCapacity})`);
            console.log(`[Robot.pickUpRawIkasmak] Cleared ikasmak.currentPiece`);
            console.log(`[Robot.pickUpRawIkasmak] Robot contains AFTER:`, this.contains);
            return true;
        }

        console.log(`[Robot.pickUpRawIkasmak] FAILED - No piece in ikasmak.currentPiece`);
        return false;
    }

    /**
     * Machine a raw material into a finished piece
     * Robot must be at Ikasmak location
     * Requires: raw material in Ikasmak, tool(s) in Ikasmak
     * Result: raw disappears, piece is created with normalized ID, tool(s) stay
     * @param {Ikasmak} ikasmak - Ikasmak entity
     * @param {string} tool - Tool(s) being used (single tool or comma-separated, max 2). Must be in Ikasmak
     * @param {string} program - Machining program
     * @param {string} pieceId - Piece ID from BT (will be normalized)
     * @returns {boolean}
     */
    machineRaw(ikasmak, tool, program, pieceId) {
        console.log(`[Robot.machineRaw] START - tool: ${tool}, program: ${program}, pieceId: ${pieceId}`);
        console.log(`[Robot.machineRaw] Robot location: ${this.location}`);
        console.log(`[Robot.machineRaw] Ikasmak contains BEFORE:`, ikasmak.contains);

        if (this.location !== "ikasmak") {
            console.log(`[Robot.machineRaw] FAILED - Robot not at ikasmak`);
            return false; // Must be at Ikasmak
        }

        // Parse tools: can be single tool or comma-separated (max 2)
        // Normalize: remove spaces around commas and tools
        const toolsArray = tool.split(',').map(t => t.trim()).filter(t => t.length > 0);

        if (toolsArray.length > 2) {
            console.log(`[Robot.machineRaw] FAILED - Too many tools specified (${toolsArray.length}), maximum is 2`);
            return false;
        }

        console.log(`[Robot.machineRaw] Parsed tools:`, toolsArray);

        // Check that ALL specified tools are in Ikasmak
        const ikasmakState = ikasmak.getState();
        for (const singleTool of toolsArray) {
            const toolExists = ikasmakState.contains.some(item => item.includes(singleTool));
            if (!toolExists) {
                console.log(`[Robot.machineRaw] FAILED - Tool ${singleTool} not found in Ikasmak`);
                return false; // Required tool not in machine
            }
        }

        console.log(`[Robot.machineRaw] All tools verified in Ikasmak: ${toolsArray.join(', ')}`);

        // Use first tool for piece naming if this is first machining
        const primaryTool = toolsArray[0];

        // Check if there's already a piece being machined (incremental machining)
        if (ikasmak.currentPiece) {
            // Incremental machining: piece already exists, just process it further
            console.log(`[Robot.machineRaw] Incremental machining on existing piece: "${ikasmak.currentPiece}"`);
            console.log(`[Robot.machineRaw] Tool(s) "${toolsArray.join(', ')}" process existing piece`);
            console.log(`[Robot.machineRaw] Tool(s) "${toolsArray.join(', ')}" STAY in Ikasmak`);
            // Piece stays as-is, tool(s) stay, nothing consumed
            console.log(`[Robot.machineRaw] Ikasmak contains AFTER:`, ikasmak.contains);
            console.log(`[Robot.machineRaw] Ikasmak currentPiece AFTER:`, ikasmak.currentPiece);
            console.log(`[Robot.machineRaw] SUCCESS - Incremental machining complete`);
            return true;
        }

        // First machining: need raw material
        // Raw = anything that's NOT a tool (T1, T2, etc.) and NOT a piece
        // Examples: "Cilíndrico Al6160", "Hexágono X", "Cuadrado Y", etc.
        const rawIndex = ikasmak.contains.findIndex(item => {
            const itemLower = item.toLowerCase();
            // Not a tool (tools start with T followed by number)
            const isTool = /^T\d+/i.test(item);
            // Not a piece (pieces contain "piece" in name)
            const isPiece = itemLower.includes('piece') || itemLower.includes('pieza');

            return !isTool && !isPiece;
        });

        if (rawIndex === -1) {
            console.log(`[Robot.machineRaw] FAILED - No raw material found in Ikasmak for first machining`);
            return false; // No raw material found in Ikasmak
        }

        // Remove the specific raw material (Cilíndrico, Hexágono, etc.)
        const removedRaw = ikasmak.contains.splice(rawIndex, 1)[0];
        console.log(`[Robot.machineRaw] Removed raw: "${removedRaw}"`);

        // Extract position from consumed raw (if it has one)
        const rawPosition = getPosition ? getPosition(removedRaw) : null;

        // Track consumed raw material
        this.consumedRaws.push(removedRaw);
        console.log(`[Robot.machineRaw] Added to consumed raws list: "${removedRaw}"`);

        // Create finished piece with incremental number AND position inherited from raw
        let piece = `piece${this.indexPieza}`;
        if (rawPosition && addPosition) {
            piece = addPosition(piece, rawPosition);
            console.log(`[Robot.machineRaw] Piece inherits position from raw: "${rawPosition}"`);
        }

        // Set piece in Ikasmak's currentPiece (NOT in contains array)
        ikasmak.currentPiece = piece;
        console.log(`[Robot.machineRaw] Created piece: "${piece}"`);
        console.log(`[Robot.machineRaw] Piece set in ikasmak.currentPiece`);
        console.log(`[Robot.machineRaw] Tool(s) "${toolsArray.join(', ')}" STAY in Ikasmak`);

        this.indexPieza++;

        console.log(`[Robot.machineRaw] Ikasmak contains AFTER:`, ikasmak.contains);
        console.log(`[Robot.machineRaw] Ikasmak currentPiece AFTER:`, ikasmak.currentPiece);
        console.log(`[Robot.machineRaw] SUCCESS - Machining complete`);

        // Tool(s) stay in Ikasmak (no removal)
        return true;
    }

    /**
     * Place tool into Ikasmak
     * @param {Ikasmak} ikasmak - Ikasmak entity
     * @param {string} herramienta - Tool ID
     * @returns {boolean}
     */
    placeToolIkasmak(ikasmak, herramienta) {
        console.log(`[Robot.placeToolIkasmak] START - tool: ${herramienta}`);
        console.log(`[Robot.placeToolIkasmak] Robot contains BEFORE:`, JSON.stringify(this.contains));
        console.log(`[Robot.placeToolIkasmak] Ikasmak contains BEFORE:`, JSON.stringify(ikasmak.contains));

        // Find tool in robot.contains (may have position suffix like "T2(2,1,2)")
        const index = this.contains.findIndex(i => i.includes(herramienta));
        if (index === -1) {
            console.log(`[Robot.placeToolIkasmak] Tool ${herramienta} not found in robot.contains`);
            return false;
        }

        if (!ikasmak.isOpen || (ikasmak.isOpen && !ikasmak.isOpen())) {
            console.log(`[Robot.placeToolIkasmak] Ikasmak not open`);
            return false;
        }

        // Get full tool WITH position
        const toolWithPos = this.contains[index];

        // Try to load in Ikasmak WITH full position tag
        const loaded = ikasmak.loadItem(toolWithPos);
        if (!loaded) {
            console.log(`[Robot.placeToolIkasmak] FAILED - Ikasmak at max tool capacity`);
            return false;
        }

        // Only remove from robot if successfully loaded
        this.contains.splice(index, 1);
        console.log(`[Robot.placeToolIkasmak] Removed from robot: "${toolWithPos}"`);
        console.log(`[Robot.placeToolIkasmak] Added to Ikasmak WITH position: "${toolWithPos}"`);
        console.log(`[Robot.placeToolIkasmak] Robot contains AFTER:`, JSON.stringify(this.contains));
        console.log(`[Robot.placeToolIkasmak] Ikasmak contains AFTER:`, JSON.stringify(ikasmak.contains));

        return true;
    }

    /**
     * Pick up tool from Ikasmak
     * @param {Ikasmak} ikasmak - Ikasmak entity
     * @param {string} herramienta - Tool ID
     * @returns {boolean}
     */
    pickUpToolIkasmak(ikasmak, herramienta) {
        console.log(`[Robot.pickUpToolIkasmak] START - tool: ${herramienta}`);
        console.log(`[Robot.pickUpToolIkasmak] Ikasmak contains BEFORE:`, ikasmak.contains);

        if (!ikasmak.isOpen()) {
            console.log(`[Robot.pickUpToolIkasmak] FAILED - Ikasmak not open`);
            return false;
        }

        // Check capacity before picking up
        if (!this.hasCapacity()) {
            console.log(`[Robot.pickUpToolIkasmak] FAILED - Robot at max capacity (${this.maxCapacity}/${this.maxCapacity})`);
            return false;
        }

        // Remove tool from Ikasmak FIRST (specify which tool to avoid duplicates)
        const removedTool = ikasmak.unloadItem(herramienta);
        if (!removedTool) {
            console.log(`[Robot.pickUpToolIkasmak] FAILED - Tool "${herramienta}" not found in Ikasmak`);
            return false;
        }

        // Then add to robot
        this.contains.push(removedTool);
        console.log(`[Robot.pickUpToolIkasmak] Picked up tool: "${removedTool}" (${this.contains.length}/${this.maxCapacity})`);
        console.log(`[Robot.pickUpToolIkasmak] Ikasmak contains AFTER:`, ikasmak.contains);
        return true;
    }

    /**
     * Place finished piece into Almacen
     * @param {Almacen} almacen - Almacen entity
     * @param {string} pieza - Piece name from BT (e.g. "Piece2")
     * @returns {boolean}
     */
    placePiece(almacen, pieza) {
        console.log(`[Robot.placePiece] Looking for piece: "${pieza}"`);
        console.log(`[Robot.placePiece] Robot contains:`, JSON.stringify(this.contains));

        // Normalize piece name: "Piece2" -> "piece2"
        const normalizedPiece = pieza.toLowerCase();

        // Find piece in robot.contains (may be like "piece2_piece")
        const index = this.contains.findIndex(i => i.toLowerCase().includes(normalizedPiece));

        if (index === -1) {
            console.log(`[Robot.placePiece] Piece containing "${normalizedPiece}" not found`);
            return false;
        }

        if (!almacen.isOpen || (almacen.isOpen && !almacen.isOpen())) {
            console.log(`[Robot.placePiece] Almacen not open`);
            return false;
        }

        const pieceItem = this.contains.splice(index, 1)[0];
        almacen.addItem(pieceItem);
        console.log(`[Robot.placePiece] Placed piece "${pieceItem}" in Almacen`);

        return true;
    }

    /**
     * Place tool into Almacen
     * @param {Almacen} almacen - Almacen entity
     * @param {string} herramienta - Tool ID
     * @returns {boolean}
     */
    placeTool(almacen, herramienta) {
        console.log(`[Robot.placeTool] Placing tool: "${herramienta}"`);
        console.log(`[Robot.placeTool] Robot contains:`, JSON.stringify(this.contains));

        // Pass herramienta to placeIntoEntity so it finds the right tool
        const result = this.placeIntoEntity(almacen, herramienta);

        if (result) {
            console.log(`[Robot.placeTool] Placed tool ${herramienta} in Almacen`);
        } else {
            console.log(`[Robot.placeTool] Failed to place tool ${herramienta}`);
        }

        return result;
    }

    /**
     * Perform measurement at metrology station
     * Removes piece from robot and stores it in measured pieces list
     * @param {Medicion} medicion - Medicion entity
     * @param {string} pieza - Piece to measure
     * @param {string} location - Current robot location
     * @returns {boolean}
     */
    measure(medicion, pieza, location) {
        console.log(`[Robot.measure] START - pieza: "${pieza}", location: "${location}"`);
        console.log(`[Robot.measure] Robot contains BEFORE:`, JSON.stringify(this.contains));

        if (location !== "metrologia") {
            console.log(`[Robot.measure] FAILED - Robot not at metrologia`);
            return false; // Must be at metrology station
        }

        // Normalize piece name for matching: "Piece1" -> "piece1", "Pieza 1" -> "piece1"
        const normalizedPieza = pieza.toLowerCase().replace(/\s+/g, '').replace(/^pieza/, 'piece');

        // Find piece in robot.contains (may be like "piece1")
        const index = this.contains.findIndex(i => i.toLowerCase().replace(/^pieza/, 'piece').includes(normalizedPieza));

        if (index === -1) {
            console.log(`[Robot.measure] FAILED - Piece containing "${normalizedPieza}" not found in robot`);
            return false;
        }

        // Remove piece from robot (piece goes to measurement zone)
        const pieceItem = this.contains.splice(index, 1)[0];
        console.log(`[Robot.measure] Removed piece "${pieceItem}" from robot`);

        // Add to measured pieces list (virtual storage, piece disappears)
        medicion.measure(pieceItem);
        console.log(`[Robot.measure] Piece "${pieceItem}" measured and stored virtually`);
        console.log(`[Robot.measure] Robot contains AFTER:`, JSON.stringify(this.contains));

        return true;
    }

    /**
     * Charge battery (requires being at cargador)
     * @param {string} location - Current robot location
     * @returns {boolean}
     */
    chargeBattery(location) {
        return location === "cargador";
    }

    /**
     * Get current piece index
     * @returns {number}
     */
    getPieceIndex() {
        return this.indexPieza;
    }

    /**
     * Get current state
     * @returns {Object}
     */
    getState() {
        // Sort items by name first, then by position (ascending)
        const sortedContains = sortItems ? sortItems([...this.contains]) : [...this.contains];
        const sortedConsumedRaws = sortItems ? sortItems([...this.consumedRaws]) : [...this.consumedRaws];

        return {
            location: this.location,
            armState: this.armState,
            contains: sortedContains,
            consumedRaws: sortedConsumedRaws
        };
    }

    /**
     * Reset to initial state
     */
    reset() {
        this.location = "cargador";
        this.armState = "reposo";
        this.contains = [];
        this.indexPieza = 1;
    }
}

// Universal export (Node.js + React)
if (typeof module !== 'undefined' && module.exports) {
    module.exports = Robot;
}
export default Robot;
